import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, AlertTriangle, FileText } from "lucide-react";
import { API } from "../lib/api";

/**
 * Public missing-documents upload page.
 * URL: /upload-dokumenter/:token
 * Applicant lands here from an admin-triggered email.
 */
export default function MissingDocsUploadPage() {
  const { token } = useParams();
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [data, setData] = React.useState(null);
  const [files, setFiles] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/docs-upload/${token}`);
      if (!res.ok) {
        const j = await res.clone().json().catch(() => ({}));
        throw new Error(j.detail || `Fejl ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => { reload(); }, [reload]);

  const setFile = (kind) => (e) => {
    setFiles((f) => ({ ...f, [kind]: e.target.files?.[0] || null }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const provided = Object.values(files).filter(Boolean);
    if (provided.length === 0) {
      toast.error("Vælg mindst ét dokument først");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });
      const res = await fetch(`${API}/docs-upload/${token}`, { method: "POST", body: fd });
      const j = await res.clone().json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `Fejl ${res.status}`);
      setDone(j);
      toast.success(`${j.uploaded_count} dokument(er) modtaget`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2">
        <Loader2 className="animate-spin text-brand-red" size={32} />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4">
        <div className="bg-white border border-brand p-10 max-w-md text-center" data-testid="docs-upload-error">
          <AlertTriangle size={40} className="text-brand-red mx-auto" />
          <h1 className="font-display font-bold text-2xl text-brand-dark mt-4">Linket virker ikke</h1>
          <p className="text-brand-text2 mt-2 text-sm">{err}</p>
          <p className="text-brand-text2 mt-4 text-xs">Kontakt <a href="mailto:job@emakurer.dk" className="underline">job@emakurer.dk</a> for et nyt link.</p>
        </div>
      </div>
    );
  }

  if (done && done.remaining_missing && done.remaining_missing.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg2 px-4 py-12">
        <div className="bg-white border border-brand p-10 max-w-xl text-center" data-testid="docs-upload-success">
          <div className="w-16 h-16 bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center rounded-full">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="font-display font-bold text-3xl text-brand-dark mt-6">Tak! Alle dokumenter modtaget</h1>
          <p className="text-brand-text2 mt-3">Vi vender tilbage til dig hurtigst muligt.</p>
        </div>
      </div>
    );
  }

  const missing = (done?.remaining_missing) || data?.missing || [];
  const labels = data?.missing_labels || {};

  return (
    <div className="min-h-screen bg-brand-bg2 px-4 py-10">
      <div className="max-w-2xl mx-auto" data-testid="docs-upload-page">
        <div className="bg-brand-dark text-white p-8 sm:p-10">
          <div className="kicker text-white/70">EMA Kurer ApS · Upload manglende dokumenter</div>
          <h1 className="font-display font-bold text-3xl tracking-tight mt-3">Hej {data?.applicant?.full_name}</h1>
          <p className="text-white/80 mt-3 text-sm leading-relaxed">
            For at færdiggøre din ansøgning mangler vi disse dokumenter. Upload dem nedenfor — vi
            modtager dem automatisk.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white border border-brand p-7 sm:p-10 space-y-5">
          {done && done.uploaded_count > 0 && (
            <div className="bg-emerald-50 border border-emerald-300 p-3 text-emerald-900 text-sm">
              ✓ {done.uploaded_count} dokument(er) modtaget. {missing.length > 0 ? `Mangler stadig: ${missing.length} dokument(er).` : ""}
            </div>
          )}

          {missing.map((kind) => (
            <div key={kind} className="border border-brand bg-brand-bg2/40 p-4">
              <label className="flex items-center gap-2 font-semibold text-brand-dark mb-2">
                <FileText size={16} />
                {labels[kind] || kind}
              </label>
              <input
                type="file"
                accept=".pdf,image/*"
                onChange={setFile(kind)}
                className="block text-sm"
                data-testid={`docs-upload-input-${kind}`}
              />
              {files[kind] && (
                <div className="text-xs text-brand-text2 mt-2 italic">{files[kind].name}</div>
              )}
            </div>
          ))}

          <button
            type="submit"
            className="btn-primary w-full sm:w-auto justify-center"
            disabled={busy}
            data-testid="docs-upload-submit"
          >
            {busy ? <><Loader2 className="animate-spin" size={16} /> Uploader…</> : <><Upload size={16} /> Send dokumenter</>}
          </button>

          <p className="text-xs text-brand-text2 mt-4">
            Filerne sendes krypteret. Vi behandler dine persondata jf. GDPR.
            Spørgsmål? Skriv til <a href="mailto:job@emakurer.dk" className="underline">job@emakurer.dk</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
